export interface S3Response {
  signedUrl: string
  publicUrl: string
  filename: string
  fileKey: string
}

export interface S3UploadOptions {
  server?: string
  signingUrl?: string
  getSignedUrl?: (
    file: File,
    callback: (params: S3Response) => void,
  ) => Promise<void> | void
  preprocess?: (file: File, next: (file: File) => void) => void
  onSignedUrl?: (result: any) => void
  onProgress?: (percent: number, status: string, file: File) => void
  onFinishS3Put?: (signResult: any, file: File) => void
  onError?: (status: string, file: File, context?: any) => void
  signingUrlMethod?: string
  signingUrlHeaders?: Record<string, string> | (() => Record<string, string>)
  signingUrlQueryParams?:
    | Record<string, string>
    | (() => Record<string, string>)
  signingUrlWithCredentials?: boolean
  uploadRequestHeaders?: Record<string, string>
  contentDisposition?: string
  s3path?: string
  scrubFilename?: (filename: string) => string
  fileElement?: HTMLInputElement | null
  files?: FileList | null
}

export default class S3Upload {
  private options: S3UploadOptions
  private httpRequest: XMLHttpRequest | null = null

  constructor(options: S3UploadOptions = {}) {
    this.options = options
    const files = options.fileElement?.files || options.files || []
    this.handleFileSelect(files)
  }

  private scrubFilename(filename: string = ''): string {
    return this.options.scrubFilename
      ? this.options.scrubFilename(filename)
      : filename.replace(/[^\w\d_\-.]+/gi, '')
  }

  private getFileMimeType(file: File): string {
    return file.type
  }

  private handleFileSelect(files: FileList | File[]) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      this.options.preprocess?.(file as File, (processedFile) => {
        this.options.onProgress?.(0, 'Waiting', processedFile)
        this.uploadFile(processedFile)
      })
    }
  }

  private createCORSRequest(
    method: string,
    url: string,
    withCredentials?: boolean,
  ): XMLHttpRequest {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url, true)
    if (withCredentials) xhr.withCredentials = true
    return xhr
  }

  private executeOnSignedUrl(file: File, callback: (result: any) => void) {
    if (this.options.getSignedUrl) {
      return this.options.getSignedUrl(file, callback)
    }

    const fileName = this.scrubFilename(file.name)
    let queryString = `?objectName=${fileName}&contentType=${encodeURIComponent(this.getFileMimeType(file))}`
    if (this.options.s3path)
      queryString += `&path=${encodeURIComponent(this.options.s3path)}`

    const xhr = this.createCORSRequest(
      this.options.signingUrlMethod || 'GET',
      `${this.options.server || ''}${this.options.signingUrl}${queryString}`,
      this.options.signingUrlWithCredentials,
    )

    if (typeof this.options.signingUrlHeaders === 'function') {
      this.options.signingUrlHeaders = this.options.signingUrlHeaders()
    }
    if (this.options.signingUrlHeaders) {
      Object.entries(this.options.signingUrlHeaders).forEach(([key, val]) => {
        xhr.setRequestHeader(key, val)
      })
    }

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200 || xhr.status === 201) {
          const result = JSON.parse(xhr.responseText)
          this.options.onSignedUrl?.(result)
          callback(result)
        } else {
          this.options.onError?.(
            `Could not get signed URL: ${xhr.status}`,
            file,
            { response: xhr.responseText },
          )
        }
      }
    }
    xhr.send()
  }

  private uploadToS3(file: File, signResult: any) {
    const xhr = this.createCORSRequest('PUT', signResult.signedUrl)
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) {
        this.options.onProgress?.(100, 'Upload completed', file)
        this.options.onFinishS3Put?.(signResult, file)
      } else {
        this.options.onError?.(`Upload error: ${xhr.status}`, file, {
          response: xhr.responseText,
        })
      }
    }

    xhr.onerror = () => {
      this.options.onError?.('XHR error', file, { response: xhr.responseText })
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percentLoaded = Math.round((e.loaded / e.total) * 100)
        this.options.onProgress?.(
          percentLoaded,
          percentLoaded === 100 ? 'Finalizing' : 'Uploading',
          file,
        )
      }
    }

    xhr.setRequestHeader('content-type', this.getFileMimeType(file))

    if (this.options.contentDisposition) {
      const disposition =
        this.options.contentDisposition === 'auto'
          ? file.type.startsWith('image/')
            ? 'inline'
            : 'attachment'
          : this.options.contentDisposition
      xhr.setRequestHeader(
        'content-disposition',
        `${disposition}; filename="${file.name}"`,
      )
    }

    if (!this.options.uploadRequestHeaders) {
      xhr.setRequestHeader('x-amz-acl', 'public-read')
    }

    this.httpRequest = xhr
    xhr.send(file)
  }

  public uploadFile(file?: File) {
    if (!file && this.options.fileElement?.files?.length) {
      file = this.options.fileElement.files[0]
    }
    if (file) {
      this.executeOnSignedUrl(file, (signResult) => {
        this.uploadToS3(file, signResult)
      })
    }
  }

  public abortUpload() {
    this.httpRequest?.abort()
  }
}
